import { useMemo, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  useGeneration,
  useGenerationStats,
  useGenerations,
  RATING_FILTERS,
  type GenerationRow,
  type GenerationVote,
  type RatingFilter,
} from "@/api/admin/generations";
import { AdminErrorState } from "@/components/admin/AdminErrorState";
import { formatNumber, formatPercent } from "@/lib/format";
import { i18n, SUPPORTED_LOCALES } from "@/i18n";
import { displayName, identLabel } from "./userIdentity";

/**
 * Generation journal (reading-feedback spec §8).
 *
 * Top to bottom, in the order the feature exists for:
 *   1. the tuning table — `operation x model x effort` with up-share and
 *      response rate, the artifact that answers "we moved `transit_day`
 *      to effort=low, did the up-share move?";
 *   2. the filters;
 *   3. the paginated journal, preview text only;
 *   4. the detail drawer — full output, prompt, config snapshot and
 *      context. Opening it writes an `audit_log` row on the server, so it
 *      only ever loads on an explicit click.
 *
 * The date range and the locale drive both the stats window and the list
 * filter, so the aggregate above and the rows below always describe the
 * same slice — at the cost of the table dropping off the rollup whenever a
 * locale is picked (`generations.stats.raw_note` says so on screen).
 */

const PAGE_SIZE = 50;
const DEFAULT_WINDOW_DAYS = 30;

const PAGE: CSSProperties = {
  padding: "16px 24px 96px",
  display: "flex",
  flexDirection: "column",
  gap: 12,
};
const TITLE: CSSProperties = {
  fontFamily: "var(--font-display)",
  fontSize: 22,
  color: "var(--d-fg)",
};
const SUB: CSSProperties = {
  fontFamily: "var(--font-body)",
  fontSize: 12,
  color: "var(--d-fg-muted)",
};
const CARD: CSSProperties = {
  background: "var(--d-ink-2)",
  border: "1px solid var(--d-line)",
  borderRadius: 22,
  padding: 16,
  overflowX: "auto",
};
const CARD_TITLE: CSSProperties = {
  fontFamily: "var(--font-display)",
  fontSize: 16,
  color: "var(--d-fg)",
  marginBottom: 6,
};
const TABLE: CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontFamily: "var(--font-mono)",
  fontSize: 12,
};
const TH: CSSProperties = {
  textAlign: "right",
  padding: "8px 10px 8px 0",
  borderBottom: "1px solid var(--d-line)",
  color: "var(--d-fg-muted)",
  fontFamily: "var(--font-body)",
  fontSize: 11,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  whiteSpace: "nowrap",
};
const TH_L: CSSProperties = { ...TH, textAlign: "left" };
const TD: CSSProperties = {
  padding: "8px 10px 8px 0",
  borderBottom: "1px solid var(--d-line)",
  color: "var(--d-fg)",
  textAlign: "right",
  fontVariantNumeric: "tabular-nums",
  whiteSpace: "nowrap",
};
const TD_L: CSSProperties = { ...TD, textAlign: "left" };
const FILTER_ROW: CSSProperties = {
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
  alignItems: "flex-end",
};
const FIELD: CSSProperties = { display: "flex", flexDirection: "column", gap: 4 };
const LABEL: CSSProperties = {
  fontFamily: "var(--font-body)",
  fontSize: 11,
  color: "var(--d-fg-muted)",
};
const CONTROL: CSSProperties = {
  height: 32,
  padding: "0 10px",
  borderRadius: 999,
  border: "1px solid var(--d-line)",
  background: "var(--d-ink-2)",
  color: "var(--d-fg)",
  fontFamily: "var(--font-body)",
  fontSize: 13,
  minWidth: 130,
};
const EMPTY: CSSProperties = {
  fontFamily: "var(--font-body)",
  fontSize: 12,
  color: "var(--d-fg-dim)",
  padding: "8px 0",
};
const PAGER: CSSProperties = {
  display: "flex",
  gap: 10,
  alignItems: "center",
  marginTop: 12,
};
const PAGER_BTN: CSSProperties = {
  height: 32,
  padding: "0 14px",
  borderRadius: 999,
  border: "1px solid var(--d-violet)",
  background: "transparent",
  color: "var(--d-violet)",
  fontFamily: "var(--font-body)",
  fontSize: 13,
  cursor: "pointer",
};
const LINK: CSSProperties = {
  color: "var(--d-violet)",
  textDecoration: "none",
};
const ROW_BTN: CSSProperties = {
  border: "none",
  background: "transparent",
  color: "var(--d-violet)",
  fontFamily: "var(--font-mono)",
  fontSize: 12,
  cursor: "pointer",
  padding: 0,
  textAlign: "left",
};
const DRAWER: CSSProperties = {
  position: "fixed",
  top: 0,
  right: 0,
  bottom: 0,
  width: "min(680px, 100vw)",
  background: "var(--d-ink-2)",
  borderLeft: "1px solid var(--d-line)",
  padding: 20,
  overflowY: "auto",
  zIndex: 50,
  display: "flex",
  flexDirection: "column",
  gap: 12,
};
const PRE: CSSProperties = {
  margin: 0,
  fontFamily: "var(--font-mono)",
  fontSize: 11,
  color: "var(--d-fg-muted)",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
};
const SECTION_TITLE: CSSProperties = {
  fontFamily: "var(--font-body)",
  fontSize: 11,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: "var(--d-fg-dim)",
};

const RATING_LABEL: Record<RatingFilter, "generations.rating.up" | "generations.rating.down" | "generations.rating.none"> = {
  up: "generations.rating.up",
  down: "generations.rating.down",
  none: "generations.rating.none",
};

function isoDay(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function defaultWindow(): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to);
  from.setUTCDate(from.getUTCDate() - (DEFAULT_WINDOW_DAYS - 1));
  return { from: isoDay(from), to: isoDay(to) };
}

function utcStamp(iso: string): string {
  return new Date(iso).toISOString().replace("T", " ").slice(0, 19);
}

/** Admin-grade precision: a single reading costs fractions of a cent. */
function formatCost(usd: number): string {
  return `$${usd.toFixed(4)}`;
}

function voteBadge(votes: GenerationVote[]): string {
  if (votes.length === 0) return "—";
  const up = votes.filter((v) => v.rating === "up").length;
  const down = votes.length - up;
  return [up > 0 ? `👍 ${up}` : "", down > 0 ? `👎 ${down}` : ""]
    .filter(Boolean)
    .join("  ");
}

function modelCell(row: { model: string; reasoning_effort: string | null; temperature: number | null }): string {
  const bits = [row.model];
  if (row.reasoning_effort) bits.push(`e=${row.reasoning_effort}`);
  if (row.temperature !== null) bits.push(`t=${row.temperature}`);
  return bits.join(" · ");
}

interface FilterState {
  operation: string;
  model: string;
  effort: string;
  locale: string;
  rating: "" | RatingFilter;
  dateFrom: string;
  dateTo: string;
}

export function GenerationsPage(): JSX.Element {
  const { t } = useTranslation("admin");
  const lng = i18n.language || "en";
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [offset, setOffset] = useState(0);
  const [filters, setFilters] = useState<FilterState>(() => {
    const win = defaultWindow();
    return {
      operation: "",
      model: "",
      effort: "",
      locale: "",
      rating: "",
      dateFrom: win.from,
      dateTo: win.to,
    };
  });

  // Any filter change invalidates the current page offset — page 4 of the
  // old result set is meaningless against the new one.
  const patch = (p: Partial<FilterState>): void => {
    setFilters((prev) => ({ ...prev, ...p }));
    setOffset(0);
  };

  // The same slice as the list below — otherwise the up-share above would
  // describe every locale while the rows describe one. A locale/platform
  // slice forces the backend off the rollup (see `stats.raw_note`).
  const statsQuery = useGenerationStats({
    since: filters.dateFrom || undefined,
    until: filters.dateTo || undefined,
    locale: filters.locale || undefined,
  });
  const listQuery = useGenerations({
    operation: filters.operation || undefined,
    model: filters.model || undefined,
    reasoningEffort: filters.effort || undefined,
    locale: filters.locale || undefined,
    rating: filters.rating || undefined,
    dateFrom: filters.dateFrom || undefined,
    dateTo: filters.dateTo || undefined,
    limit: PAGE_SIZE,
    offset,
  });

  const statsRows = useMemo(() => {
    const rows = statsQuery.data?.rows ?? [];
    return [...rows].sort((a, b) => b.generations - a.generations);
  }, [statsQuery.data]);

  // The filter selects are populated from the stats of the same window:
  // a configuration absent from the aggregate has no rows in the list
  // either, so this is the exact set of values worth offering.
  const options = useMemo(() => {
    const ops = new Set<string>();
    const models = new Set<string>();
    const efforts = new Set<string>();
    for (const r of statsQuery.data?.rows ?? []) {
      ops.add(r.operation);
      models.add(r.model);
      if (r.reasoning_effort) efforts.add(r.reasoning_effort);
    }
    const withCurrent = (set: Set<string>, current: string): string[] => {
      if (current) set.add(current);
      return [...set].sort();
    };
    return {
      operations: withCurrent(ops, filters.operation),
      models: withCurrent(models, filters.model),
      efforts: withCurrent(efforts, filters.effort),
    };
  }, [statsQuery.data, filters.operation, filters.model, filters.effort]);

  const list = listQuery.data;
  const items: GenerationRow[] = list?.items ?? [];
  const total = list?.total ?? 0;

  return (
    <div style={PAGE}>
      <div>
        <h2 style={TITLE}>{t("generations.title")}</h2>
        <div style={SUB}>{t("generations.subtitle")}</div>
      </div>

      <div style={CARD} data-testid="generations-stats">
        <div style={CARD_TITLE}>{t("generations.stats.title")}</div>
        <div style={SUB}>{t("generations.stats.note")}</div>
        {statsQuery.data?.source === "raw" && (
          <div style={SUB} data-testid="generations-stats-raw-note">
            {t("generations.stats.raw_note")}
          </div>
        )}
        {statsQuery.isError ? (
          <AdminErrorState onRetry={() => void statsQuery.refetch()} />
        ) : statsQuery.isLoading ? (
          <div style={EMPTY}>{t("common.loading")}</div>
        ) : statsRows.length === 0 ? (
          <div style={EMPTY}>{t("generations.no_data")}</div>
        ) : (
          <table style={TABLE}>
            <thead>
              <tr>
                <th style={TH_L}>{t("generations.stats_col.operation")}</th>
                <th style={TH_L}>{t("generations.stats_col.model")}</th>
                <th style={TH_L}>{t("generations.stats_col.effort")}</th>
                <th style={TH}>{t("generations.stats_col.generations")}</th>
                <th style={TH}>{t("generations.stats_col.votes")}</th>
                <th style={TH}>{t("generations.stats_col.up")}</th>
                <th style={TH}>{t("generations.stats_col.down")}</th>
                <th style={TH}>{t("generations.stats_col.up_share")}</th>
                <th style={TH}>{t("generations.stats_col.response_rate")}</th>
              </tr>
            </thead>
            <tbody>
              {statsRows.map((r) => (
                <tr key={`${r.operation}|${r.model}|${r.reasoning_effort ?? ""}`}>
                  <td style={TD_L}>{r.operation}</td>
                  <td style={TD_L}>{r.model}</td>
                  <td style={TD_L}>{r.reasoning_effort ?? "—"}</td>
                  <td style={TD}>{formatNumber(r.generations, lng)}</td>
                  <td style={TD}>{formatNumber(r.votes, lng)}</td>
                  <td style={TD}>{formatNumber(r.up_votes, lng)}</td>
                  <td style={TD}>{formatNumber(r.down_votes, lng)}</td>
                  <td style={TD}>
                    {r.up_share_pct === null ? "—" : formatPercent(r.up_share_pct, lng, 1)}
                  </td>
                  <td style={TD}>
                    {r.response_rate_pct === null
                      ? "—"
                      : formatPercent(r.response_rate_pct, lng, 1)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={FILTER_ROW} data-testid="generations-filters">
        <label style={FIELD}>
          <span style={LABEL}>{t("generations.filter.operation")}</span>
          <select
            aria-label={t("generations.filter.operation")}
            style={CONTROL}
            value={filters.operation}
            onChange={(e) => patch({ operation: e.target.value })}
          >
            <option value="">{t("generations.filter.all")}</option>
            {options.operations.map((v) => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
        </label>
        <label style={FIELD}>
          <span style={LABEL}>{t("generations.filter.model")}</span>
          <select
            aria-label={t("generations.filter.model")}
            style={CONTROL}
            value={filters.model}
            onChange={(e) => patch({ model: e.target.value })}
          >
            <option value="">{t("generations.filter.all")}</option>
            {options.models.map((v) => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
        </label>
        <label style={FIELD}>
          <span style={LABEL}>{t("generations.filter.effort")}</span>
          <select
            aria-label={t("generations.filter.effort")}
            style={CONTROL}
            value={filters.effort}
            onChange={(e) => patch({ effort: e.target.value })}
          >
            <option value="">{t("generations.filter.all")}</option>
            {options.efforts.map((v) => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
        </label>
        <label style={FIELD}>
          <span style={LABEL}>{t("generations.filter.locale")}</span>
          <select
            aria-label={t("generations.filter.locale")}
            style={CONTROL}
            value={filters.locale}
            onChange={(e) => patch({ locale: e.target.value })}
          >
            <option value="">{t("generations.filter.all")}</option>
            {SUPPORTED_LOCALES.map((v) => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
        </label>
        <label style={FIELD}>
          <span style={LABEL}>{t("generations.filter.rating")}</span>
          <select
            aria-label={t("generations.filter.rating")}
            style={CONTROL}
            value={filters.rating}
            onChange={(e) => patch({ rating: e.target.value as "" | RatingFilter })}
          >
            <option value="">{t("generations.filter.all")}</option>
            {RATING_FILTERS.map((v) => (
              <option key={v} value={v}>{t(RATING_LABEL[v])}</option>
            ))}
          </select>
        </label>
        <label style={FIELD}>
          <span style={LABEL}>{t("generations.filter.date_from")}</span>
          <input
            type="date"
            aria-label={t("generations.filter.date_from")}
            style={CONTROL}
            value={filters.dateFrom}
            onChange={(e) => patch({ dateFrom: e.target.value })}
          />
        </label>
        <label style={FIELD}>
          <span style={LABEL}>{t("generations.filter.date_to")}</span>
          <input
            type="date"
            aria-label={t("generations.filter.date_to")}
            style={CONTROL}
            value={filters.dateTo}
            onChange={(e) => patch({ dateTo: e.target.value })}
          />
        </label>
      </div>

      <div style={CARD} data-testid="generations-list">
        <div style={CARD_TITLE}>{t("generations.list.title")}</div>
        {listQuery.isError ? (
          <AdminErrorState onRetry={() => void listQuery.refetch()} />
        ) : listQuery.isLoading ? (
          <div style={EMPTY}>{t("common.loading")}</div>
        ) : items.length === 0 ? (
          <div style={EMPTY}>{t("generations.no_data")}</div>
        ) : (
          <>
            <table style={TABLE}>
              <thead>
                <tr>
                  <th style={TH_L}>{t("generations.col.when")}</th>
                  <th style={TH_L}>{t("generations.col.user")}</th>
                  <th style={TH_L}>{t("generations.col.operation")}</th>
                  <th style={TH_L}>{t("generations.col.model")}</th>
                  <th style={TH}>{t("generations.col.tokens")}</th>
                  <th style={TH}>{t("generations.col.cost")}</th>
                  <th style={TH}>{t("generations.col.latency")}</th>
                  <th style={TH_L}>{t("generations.col.vote")}</th>
                  <th style={TH_L}>{t("generations.col.preview")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.id} data-testid={`generation-row-${row.id}`}>
                    <td style={TD_L}>{utcStamp(row.created_at)}</td>
                    <td style={TD_L}>
                      {row.user === null ? (
                        "—"
                      ) : (
                        <Link style={LINK} to={`/admin/users/${row.user.id}`}>
                          {displayName(row.user) ?? identLabel(row.user)}
                        </Link>
                      )}
                    </td>
                    <td style={TD_L}>{row.operation}</td>
                    <td style={TD_L}>{modelCell(row)}</td>
                    <td style={TD}>
                      {formatNumber(row.tokens_in, lng)} / {formatNumber(row.tokens_out, lng)}
                    </td>
                    <td style={TD}>{formatCost(row.cost_usd)}</td>
                    <td style={TD}>{formatNumber(row.latency_ms, lng)}</td>
                    <td style={TD_L}>{voteBadge(row.votes)}</td>
                    <td style={{ ...TD_L, whiteSpace: "normal", minWidth: 260 }}>
                      <button
                        type="button"
                        style={ROW_BTN}
                        onClick={() => setSelectedId(row.id)}
                      >
                        {row.preview || t("generations.open")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={PAGER}>
              <button
                type="button"
                style={PAGER_BTN}
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                {t("generations.prev")}
              </button>
              <button
                type="button"
                style={PAGER_BTN}
                disabled={offset + PAGE_SIZE >= total}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                {t("generations.next")}
              </button>
              <span style={SUB} data-testid="generations-range">
                {t("generations.range", {
                  from: formatNumber(offset + 1, lng),
                  to: formatNumber(offset + items.length, lng),
                  total: formatNumber(total, lng),
                })}
              </span>
            </div>
          </>
        )}
      </div>

      {selectedId !== null && (
        <GenerationDrawer id={selectedId} onClose={() => setSelectedId(null)} />
      )}
    </div>
  );
}

function GenerationDrawer({ id, onClose }: { id: number; onClose: () => void }): JSX.Element {
  const { t } = useTranslation("admin");
  const lng = i18n.language || "en";
  const { data, isLoading, isError, refetch } = useGeneration(id);

  return (
    <aside style={DRAWER} data-testid="generation-drawer" aria-label={t("generations.detail.title")}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={CARD_TITLE}>{t("generations.detail.title", { id })}</div>
        <button type="button" style={PAGER_BTN} onClick={onClose}>
          {t("generations.detail.close")}
        </button>
      </div>

      {isError ? (
        <AdminErrorState onRetry={() => void refetch()} />
      ) : isLoading || !data ? (
        <div style={EMPTY}>{t("common.loading")}</div>
      ) : (
        <>
          <div style={SECTION_TITLE}>{t("generations.detail.config")}</div>
          <table style={TABLE}>
            <tbody>
              {([
                ["generations.detail.field.when", utcStamp(data.created_at)],
                ["generations.detail.field.user", data.user_id === null ? "—" : `#${data.user_id}`],
                ["generations.detail.field.operation", data.operation],
                ["generations.detail.field.model", data.model],
                ["generations.detail.field.effort", data.reasoning_effort ?? "—"],
                ["generations.detail.field.temperature",
                  data.temperature === null ? "—" : String(data.temperature)],
                ["generations.detail.field.max_tokens", formatNumber(data.max_tokens, lng)],
                ["generations.detail.field.template", data.prompt_template],
                ["generations.detail.field.locale", data.locale],
                ["generations.detail.field.platform", data.platform],
                ["generations.detail.field.tokens",
                  `${formatNumber(data.tokens_in, lng)} / ${formatNumber(data.tokens_out, lng)}` +
                  (data.reasoning_tokens === null
                    ? ""
                    : ` (+${formatNumber(data.reasoning_tokens, lng)})`)],
                ["generations.detail.field.cost", formatCost(data.cost_usd)],
                ["generations.detail.field.latency", formatNumber(data.latency_ms, lng)],
                ["generations.detail.field.reading",
                  data.reading_id === null ? "—" : `#${data.reading_id}`],
                ["generations.detail.field.prompt_hash", data.prompt_hash],
              ] as const).map(([key, value]) => (
                <tr key={key}>
                  <td style={{ ...TD_L, color: "var(--d-fg-muted)", width: 160 }}>{t(key)}</td>
                  <td style={{ ...TD_L, whiteSpace: "normal", wordBreak: "break-all" }}>{value}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={SECTION_TITLE}>{t("generations.detail.votes")}</div>
          {data.votes.length === 0 ? (
            <div style={EMPTY}>{t("generations.detail.no_votes")}</div>
          ) : (
            <table style={TABLE}>
              <tbody>
                {data.votes.map((v) => (
                  <tr key={`${v.user_id ?? "anon"}-${v.created_at}`}>
                    <td style={TD_L}>{v.rating === "up" ? "👍" : "👎"}</td>
                    <td style={TD_L}>{v.user_id === null ? "—" : `#${v.user_id}`}</td>
                    <td style={TD_L}>{v.reason ?? "—"}</td>
                    <td style={TD_L}>{utcStamp(v.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div style={SECTION_TITLE}>{t("generations.detail.output")}</div>
          <pre style={{ ...PRE, color: "var(--d-fg)" }} data-testid="generation-output">
            {data.output_text}
          </pre>

          <div style={SECTION_TITLE}>{t("generations.detail.system_prompt")}</div>
          {data.system_prompt === null ? (
            <div style={EMPTY}>{t("generations.detail.prompt_not_stored")}</div>
          ) : (
            <pre style={PRE}>{data.system_prompt}</pre>
          )}

          <div style={SECTION_TITLE}>{t("generations.detail.user_prompt")}</div>
          {data.user_prompt === null ? (
            <div style={EMPTY}>{t("generations.detail.prompt_not_stored")}</div>
          ) : (
            <pre style={PRE}>{data.user_prompt}</pre>
          )}

          <div style={SECTION_TITLE}>{t("generations.detail.context")}</div>
          {data.context === null ? (
            <div style={EMPTY}>{t("generations.detail.no_context")}</div>
          ) : (
            <pre style={PRE}>{JSON.stringify(data.context, null, 2)}</pre>
          )}
        </>
      )}
    </aside>
  );
}
